import type { CharacterState, Rules } from "@/data/types";
import { calcDerivedStats } from "@/lib/characterCalc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Minus, Check, Wand2, Shuffle } from "lucide-react";

export function StepSpellAllocation({
  state,
  rules,
  stats,
  onChange,
}: {
  state: CharacterState;
  rules: Rules;
  stats: ReturnType<typeof calcDerivedStats>;
  onChange: (s: CharacterState) => void;
}) {
  const budget = stats.totalSpellPointBudget;
  const totalBudget =
    budget.any +
    budget.destruction +
    budget.warMagic +
    budget.protection +
    budget.sorcery;

  const getPoints = (spellId: string) =>
    state.spellAllocations.find((a) => a.spellId === spellId)?.points ?? 0;

  const setPoints = (spellId: string, points: number) => {
    const others = state.spellAllocations.filter((a) => a.spellId !== spellId);
    const newAllocs = points > 0 ? [...others, { spellId, points }] : others;
    onChange({ ...state, spellAllocations: newAllocs });
  };

  const randomiseAll = () => {
    const allSpells = rules.spellLists
      .flatMap((l) => l.spells)
      .filter((s) => !s.deprecated);

    const counts: Record<string, number> = {};
    const simUsed: Record<string, number> = {};

    const getSimRemaining = (listId: string) => {
      const dedicated = budget[listId as keyof typeof budget] ?? 0;
      const used = simUsed[listId] ?? 0;
      const usedDedicated = Math.min(used, dedicated);
      const overflow = Math.max(0, used - dedicated);
      const anyUsedByOthers = Object.keys(simUsed)
        .filter((lid) => lid !== listId)
        .reduce((sum, lid) => {
          return (
            sum +
            Math.max(
              0,
              (simUsed[lid] ?? 0) - (budget[lid as keyof typeof budget] ?? 0)
            )
          );
        }, 0);
      const anyRemaining = budget.any - anyUsedByOthers - overflow;
      return dedicated - usedDedicated + Math.max(0, anyRemaining);
    };

    for (;;) {
      const eligible = allSpells.filter((spell) => {
        const pts = counts[spell.id] ?? 0;
        if (spell.scaling === "binary" && pts >= 1) return false;
        if (stats.maxSpellLevel !== null && pts >= stats.maxSpellLevel)
          return false;
        return getSimRemaining(spell.listId) >= 1;
      });
      if (eligible.length === 0) break;
      const pick = eligible[Math.floor(Math.random() * eligible.length)];
      counts[pick.id] = (counts[pick.id] ?? 0) + 1;
      simUsed[pick.listId] = (simUsed[pick.listId] ?? 0) + 1;
    }

    const newAllocs = Object.entries(counts).map(([spellId, points]) => ({
      spellId,
      points,
    }));
    onChange({ ...state, spellAllocations: newAllocs });
  };

  // Compute used points per list
  const usedByList: Record<string, number> = {
    any: 0,
    destruction: 0,
    warMagic: 0,
    protection: 0,
    sorcery: 0,
  };
  for (const alloc of state.spellAllocations) {
    if (alloc.points <= 0) continue;
    const spell = rules.spellLists
      .flatMap((l) => l.spells)
      .find((s) => s.id === alloc.spellId);
    if (!spell) continue;
    // "any" budget is consumed first conceptually, but we track it by listId
    usedByList[spell.listId] = (usedByList[spell.listId] ?? 0) + alloc.points;
  }

  // Available budget for a spell in a given list:
  // listId-specific budget + "any" budget - already used from "any" that went to this list
  const getRemainingForList = (listId: string) => {
    const dedicated = budget[listId as keyof typeof budget] ?? 0;
    const usedDedicated = Math.min(usedByList[listId] ?? 0, dedicated);
    const overflow = Math.max(0, (usedByList[listId] ?? 0) - dedicated);
    const anyRemaining =
      budget.any -
      Object.keys(usedByList).reduce((sum, lid) => {
        if (lid === listId) return sum;
        return (
          sum +
          Math.max(
            0,
            (usedByList[lid] ?? 0) - (budget[lid as keyof typeof budget] ?? 0)
          )
        );
      }, 0) -
      overflow;
    return dedicated - usedDedicated + Math.max(0, anyRemaining);
  };

  if (totalBudget === 0) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-bold mb-1">Spell Allocation</h2>
        </div>
        <div className="text-center py-8 text-muted-foreground text-sm border-2 border-dashed rounded-lg">
          <Wand2 className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p>You have no spell points to allocate.</p>
          <p className="mt-1">
            Take Arcane Prodigy (Core Skill) or spell-granting Otherworldly
            Gifts to gain access to magic.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold mb-1">Spell Allocation</h2>
          <p className="text-muted-foreground text-sm">
            Allocate your spell points. &quot;Any&quot; points can be spent in
            any spell list.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={randomiseAll}
        >
          <Shuffle className="h-4 w-4 mr-1.5" />
          Randomise
        </Button>
      </div>

      {/* Budget summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {[
          { key: "any", label: "Any List" },
          { key: "destruction", label: "Destruction" },
          { key: "warMagic", label: "War Magic" },
          { key: "protection", label: "Protection" },
          { key: "sorcery", label: "Sorcery" },
        ].map(({ key, label }) => {
          const total = budget[key as keyof typeof budget] ?? 0;
          if (total === 0) return null;
          let usedFromPool: number;
          if (key === "any") {
            // "any" pool absorbs overflow from dedicated pools
            const totalOverflow = Object.keys(usedByList)
              .filter((lid) => lid !== "any")
              .reduce((sum, lid) => {
                const ded = budget[lid as keyof typeof budget] ?? 0;
                return sum + Math.max(0, (usedByList[lid] ?? 0) - ded);
              }, 0);
            usedFromPool = totalOverflow;
          } else {
            usedFromPool = Math.min(usedByList[key] ?? 0, total);
          }
          const remaining = total - usedFromPool;
          return (
            <div key={key} className="border rounded-md p-2 text-center">
              <div
                className={`text-lg font-bold ${remaining === 0 ? "text-muted-foreground" : "text-primary"}`}
              >
                {remaining}/{total}
              </div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </div>
          );
        })}
      </div>

      {/* Spell lists */}
      {rules.spellLists.map((list) => {
        const dedicated = budget[list.id as keyof typeof budget] ?? 0;
        const anyPool = budget.any;
        const available = dedicated + anyPool;
        if (available === 0 && dedicated === 0) return null;

        const remaining = getRemainingForList(list.id);

        return (
          <Card key={list.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">{list.name}</CardTitle>
                <span className="text-xs text-muted-foreground">
                  {dedicated > 0 ? `${dedicated} dedicated` : ""}
                  {dedicated > 0 && anyPool > 0 ? " + " : ""}
                  {anyPool > 0 ? `${anyPool} any` : ""}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {list.spells.map((spell) => {
                const pts = getPoints(spell.id);

                // Deprecated spells: only show if previously allocated, with remove-only UI
                if (spell.deprecated) {
                  if (pts === 0) return null;
                  return (
                    <div
                      key={spell.id}
                      className="flex items-start justify-between gap-3 py-2 border-b last:border-b-0 border-destructive/30 bg-destructive/5 rounded px-2"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm line-through text-muted-foreground">
                            {spell.name}
                          </span>
                          <Badge variant="destructive" className="text-xs">
                            Removed
                          </Badge>
                        </div>
                        <p className="text-xs text-destructive mt-0.5">
                          This spell has been removed. Clear your points and
                          choose another spell.
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setPoints(spell.id, 0)}
                        className="flex-shrink-0"
                      >
                        Clear ({pts} pts)
                      </Button>
                    </div>
                  );
                }

                if (spell.scaling === "binary") {
                  const learned = pts > 0;
                  const canLearn = !learned && remaining >= 1;
                  return (
                    <div
                      key={spell.id}
                      className="flex items-start justify-between gap-3 py-2 border-b last:border-b-0"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">
                            {spell.name}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {spell.duration}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            1 pt
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {spell.effect}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant={learned ? "secondary" : "default"}
                        onClick={() => setPoints(spell.id, learned ? 0 : 1)}
                        disabled={!learned && !canLearn}
                        className="flex-shrink-0"
                      >
                        {learned ? (
                          <>
                            <Check className="h-3 w-3 mr-1" />
                            Known
                          </>
                        ) : (
                          "Learn"
                        )}
                      </Button>
                    </div>
                  );
                }

                // multicast or stacking
                const canIncrease =
                  remaining >= 1 &&
                  (stats.maxSpellLevel === null || pts < stats.maxSpellLevel);
                return (
                  <div
                    key={spell.id}
                    className="flex items-start justify-between gap-3 py-2 border-b last:border-b-0"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">
                          {spell.name}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {spell.duration}
                        </Badge>
                        <Badge
                          variant={
                            spell.scaling === "stacking"
                              ? "warning"
                              : "secondary"
                          }
                          className="text-xs"
                        >
                          {spell.scaling}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {spell.effect}
                      </p>
                      {spell.scalingDescription && pts > 0 && (
                        <p className="text-xs text-primary mt-0.5">
                          {spell.scalingDescription}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={() =>
                          setPoints(spell.id, Math.max(0, pts - 1))
                        }
                        disabled={pts === 0}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-6 text-center text-sm font-medium">
                        {pts}
                      </span>
                      <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={() => setPoints(spell.id, pts + 1)}
                        disabled={!canIncrease}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
              <div className="text-xs text-muted-foreground">
                Remaining for this list: {remaining}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
